
import React from 'react';
import { useEmailTemplatesList } from '@/hooks/useEmailTemplatesList';
import { EmailPreviewDialog } from '@/components/admin/settings/EmailPreviewDialog';
import { CreateEmailTemplateDialog } from '@/components/admin/settings/CreateEmailTemplateDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TemplateList } from './TemplateList';
import { TemplateSearch } from './TemplateSearch';
import { TemplateTabs } from './TemplateTabs';
import { VariablesInfo } from './VariablesInfo';
import { TestEmailDialog } from './TestEmailDialog';
import { TabContentSkeleton } from '../email-editor/TabContentSkeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { TemplatesPagination } from './TemplatesPagination';

// Prevent a common category color bug by statically defining them
const emailCategoryColors: Record<string, string> = {
  onboarding: 'bg-blue-500',
  account: 'bg-purple-500',
  courses: 'bg-green-500',
  certificates: 'bg-orange-500',
  notifications: 'bg-slate-500',
  transaction: 'bg-emerald-500',
  marketing: 'bg-rose-500',
  payments: 'bg-emerald-500' // Added payments category with emerald color
};

export function EmailTemplatesContainer() {
  const {
    activeTab,
    searchQuery,
    isTestEmailModalOpen,
    setIsTestEmailModalOpen,
    testEmailAddress,
    setTestEmailAddress,
    isPreviewDialogOpen,
    setIsPreviewDialogOpen,
    previewTemplateId,
    isCreateTemplateOpen,
    setIsCreateTemplateOpen,
    isLoading,
    hasError,
    loading,
    templates,
    filteredTemplates,
    categoryOptions,
    pagination,
    currentPage,
    loadingAction,
    handleOpenTestEmailModal,
    handleSendTestEmail,
    handleTemplateCreated,
    handleSearchChange,
    handleTabChange,
    handleResetFilters,
    handleRetry,
    handlePageChange,
    handleOpenPreview,
    handleCreateTemplate
  } = useEmailTemplatesList();

  return (
    <div 
      className="space-y-4 transition-opacity duration-300 max-w-[1200px] mx-auto" 
      style={{ 
        opacity: isLoading ? 0.7 : 1,
        position: 'relative'
      }}
    >
      {isLoading && !hasError ? (
        <div className="min-h-[600px]">
          <TabContentSkeleton />
        </div>
      ) : hasError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load email templates. This may be due to insufficient browser resources or a network issue.
          </AlertDescription>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2" 
            onClick={handleRetry}
          >
            Retry
          </Button>
        </Alert>
      ) : (
        <ScrollArea className="email-templates-scroll min-h-[500px] max-h-[700px]" type="always">
          <div className="template-content-container pb-8 px-4">
            <TemplateSearch 
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onCreateTemplate={handleCreateTemplate}
            />
            
            <TemplateTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              categoryOptions={categoryOptions}
            />
            
            <VariablesInfo />
            
            <TemplateList
              loading={loading}
              templates={templates}
              filteredTemplates={filteredTemplates}
              searchQuery={searchQuery}
              emailCategoryColors={emailCategoryColors}
              onEditTemplate={(id) => window.location.href = `/email-templates/${id}`}
              onPreviewTemplate={handleOpenPreview}
              onTestEmail={handleOpenTestEmailModal}
              onResetFilters={handleResetFilters}
            />

            {pagination.totalPages > 1 && (
              <TemplatesPagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </ScrollArea>
      )}
      
      {/* Test Email Dialog */}
      <TestEmailDialog
        open={isTestEmailModalOpen}
        onOpenChange={setIsTestEmailModalOpen}
        testEmailAddress={testEmailAddress}
        onTestEmailAddressChange={setTestEmailAddress}
        onSendTestEmail={handleSendTestEmail}
        loadingAction={loadingAction}
      />

      {/* Email Preview Dialog */}
      <EmailPreviewDialog
        open={isPreviewDialogOpen}
        onOpenChange={setIsPreviewDialogOpen}
        templateId={previewTemplateId}
      />

      {/* Create Email Template Dialog */}
      <CreateEmailTemplateDialog
        open={isCreateTemplateOpen}
        onOpenChange={setIsCreateTemplateOpen}
        onCreated={handleTemplateCreated}
      />
    </div>
  );
}
